(function () {
  class PartFinder {
    constructor(root) {
      this.root = root;
      this.body = document.body;
      this.isReady = !!window.algoliasearch;

      if (!this.isReady) {
        console.error('Algolia library is not loaded.');
        return;
      }

      this.config = {
        appId: root.dataset.algoliaAppId,
        searchKey: root.dataset.algoliaSearchKey,
        indexName: root.dataset.algoliaIndexName,
        productKey: root.dataset.algoliaProductKey || '',
        productIndex: root.dataset.algoliaProductIndex || 'shopify_products',
        registrationApi: root.dataset.registrationApi,
        collectionUrl: root.dataset.collectionUrl || '/collections/all',
        shopDomain: window.Shopify?.shop || window.location.hostname
      };

      this.client = window.algoliasearch(this.config.appId, this.config.searchKey);
      this.index = this.client.initIndex(this.config.indexName);

      if (this.config.productKey) {
        this.productClient = window.algoliasearch(this.config.appId, this.config.productKey);
        this.productIndex = this.productClient.initIndex(this.config.productIndex);
      }

      this.state = {
        brandId: '',
        brandName: '',
        modelId: '',
        modelName: '',
        year: '',
        regNumber: ''
      };

      this.productState = {
        currentFilter: '',
        currentSort: 'best_seller',
        currentPage: 0,
        _renderedHandles: new Set(),
        totalHits: 0,
        hitsPerPage: 24,
        allHits: [],
        viewMode: 'grid',
        activeFacets: {}
      };

      this.cache = {
        brands: [],
        brandDataById: new Map()
      };

      this.activeList = null;
      this.activeIndex = -1;
      this.toastTimer = null;
      this._isTransitioning = false;
      this.carImages = []; // Preloaded car images lookup

      this.elements = {
        bar: this.root.querySelector('[data-partfinder-bar]'),
        spotlight: this.root.querySelector('[data-spotlight]'),
        manualWrapper: this.root.querySelector('[data-manual-wrapper]'),
        mobileOpen: this.root.querySelector('[data-mobile-open]'),
        mobileClose: this.root.querySelector('[data-mobile-close]'),
        mobileTitle: this.root.querySelector('[data-mobile-title]'),
        breadcrumbs: this.root.querySelector('[data-breadcrumbs]'),
        toast: this.root.querySelector('[data-toast]'),

        registrationInput: this.root.querySelector('[data-registration-input]'),
        registrationSubmit: this.root.querySelector('[data-registration-submit]'),

        brandInput: this.root.querySelector('[data-brand-input]'),
        modelInput: this.root.querySelector('[data-model-input]'),
        yearInput: this.root.querySelector('[data-year-input]'),

        brandList: this.root.querySelector('[data-list="brands"]'),
        modelList: this.root.querySelector('[data-list="models"]'),
        yearList: this.root.querySelector('[data-list="years"]'),

        manualSubmit: this.root.querySelector('[data-manual-submit]'),

        stepBrand: this.root.querySelector('[data-step="brand"]'),
        stepModel: this.root.querySelector('[data-step="model"]'),
        stepYear: this.root.querySelector('[data-step="year"]'),

        fieldWraps: this.root.querySelectorAll('.partfinder__field-wrap'),
        focusButtons: this.root.querySelectorAll('[data-focus-input]'),
        backButtons: this.root.querySelectorAll('[data-back-to]'),

        resultsContainer: this.root.querySelector('[data-results-container]'),
        resultsGrid: this.root.querySelector('[data-results-grid]'),
        resultsCount: this.root.querySelector('[data-results-count]'),
        resultsSort: this.root.querySelector('[data-results-sort]'),
        resultsPagination: this.root.querySelector('[data-results-pagination]'),
        loadMoreBtn: this.root.querySelector('[data-load-more]'),
        filtersSidebar: this.root.querySelector('[data-filters-sidebar]'),
        filterToggleBtn: this.root.querySelector('[data-pf-filter-toggle]'),
        carHeading: this.root.querySelector('[data-results-car-heading]')
      };

      this.requestData = { query: '', hitsPerPage: 1000, page: 0 };

      this._debouncedFilterBrand = this._debounce(() => {
        this.filterVisibleList(this.elements.brandInput, this.elements.brandList);
      }, 150);
      this._debouncedFilterModel = this._debounce(() => {
        this.filterVisibleList(this.elements.modelInput, this.elements.modelList);
      }, 150);
      this._debouncedFilterYear = this._debounce(() => {
        this.filterVisibleList(this.elements.yearInput, this.elements.yearList);
      }, 150);

      this.sortIndices = {
        best_seller: 'best_seller',
        price_asc: 'price_asc',
        price_desc: 'price_desc',
        title_asc: 'title_asc',
        title_desc: 'title_desc',
        created_asc: 'created_asc',
        created_desc: 'created_desc'
      };

      /* ── Custom sorts from Shopify metafield ── */
      this.customSorts = [];
      try {
        const raw = window.storeCustomSorts;
        if (raw && raw.sorts) {
          this.customSorts = raw.sorts;
          if (this.customSorts.length > 0) {
            this.productState.currentSort = 'custom_' + this.customSorts[0].sort_key;
          }
        }
      } catch (e) { console.warn('Could not load custom sorts:', e); }
    }

    /* ══════════ UTILITIES ══════════ */

    _debounce(fn, ms = 150) {
      let timer;
      return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn.apply(this, args), ms); };
    }

    _sanitize(value) {
      if (typeof value !== 'string') return '';
      return value.replace(/[<>"'&]/g, '').trim().slice(0, 200);
    }

    _setExpanded(input, expanded) {
      if (input) input.setAttribute('aria-expanded', String(expanded));
    }

    _setActiveDescendant(input, id) {
      if (!input) return;
      if (id) input.setAttribute('aria-activedescendant', id);
      else input.removeAttribute('aria-activedescendant');
    }

    _formatPrice(price) {
      const num = parseFloat(price);
      if (isNaN(num)) return '0,00 kr';
      return num.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';
    }

    _getStockStatus(hit) {
      const shopifyStock = parseInt(hit.shopify_stock_status || 0);
      const warehouseStatus = hit.warehouse_stock_status || '';
      if (shopifyStock > 0) return { text: 'På lager', className: 'in-stock', icon: '☰', shopifyStock };
      if (warehouseStatus && warehouseStatus !== '0') {
        const dateMatch = warehouseStatus.match(/\d{1,2}[\.\-\/]\d{1,2}[\.\-\/]\d{2,4}/);
        if (dateMatch) return { text: 'Forventet: ' + dateMatch[0], className: 'expected', icon: '☰', shopifyStock };
        return { text: warehouseStatus, className: 'expected', icon: '☰', shopifyStock };
      }
      return { text: 'Bestillingsvare', className: 'order', icon: '☰', shopifyStock: 0 };
    }

    _getIntroduction(hit) {
      if (hit.metafields && Array.isArray(hit.metafields)) {
        const intro = hit.metafields.find(m => m.key === 'product_introduction');
        if (intro) return intro.value;
      }
      if (hit.additional_data && hit.additional_data.introduction) return hit.additional_data.introduction;
      return '';
    }

    _getWarehouseInfo(hit) {
      const ws = hit.warehouse_stock_status;
      if (!ws || (typeof ws === 'string' && ws.trim() === '')) return { quantity: null, expectedDate: null, orderDays: null, rawStatus: null };
      const parsed = parseInt(ws, 10);
      if (!isNaN(parsed)) return { quantity: parsed, expectedDate: null, orderDays: null, rawStatus: null };
      const dateMatch = String(ws).match(/\b\d{2}\.\d{2}\.\d{4}\b/);
      if (dateMatch) return { quantity: null, expectedDate: dateMatch[0], orderDays: null, rawStatus: null };
      const daysMatch = String(ws).match(/(\d+)\s*dager/i);
      if (daysMatch) return { quantity: null, expectedDate: null, orderDays: parseInt(daysMatch[1], 10), rawStatus: null };
      return { quantity: null, expectedDate: null, orderDays: null, rawStatus: String(ws).trim() };
    }

    /* ══════════ INIT & EVENTS ══════════ */

    init() {
      this._initAria();
      this._initPlateInput();
      this.bindEvents();
      this._injectCustomSortOptions();
      this._initViewToggle();
      this._preloadCarImages();
      this._initPopstate();
      this._initPageshow();

      /* If URL has search params, show results container immediately */
      const params = new URLSearchParams(window.location.search);
      if (params.get('pf_brand') && params.get('pf_model') && params.get('pf_year')) {
        const container = this.elements.resultsContainer;
        if (container) container.style.display = '';
        document.body.classList.add('pf-results-active');
        if (document.querySelector('.pf-brand-grid-wrapper')) {
          document.body.classList.add('pf-brand-grid-hidden');
          document.querySelector('.pf-brand-grid-wrapper')?.classList.add('pf-brand-grid-hidden');
        }
      }

      this.loadBrands().then(() => {
        this._restoreFromUrl();
      });
    }

    _initPageshow() {
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          /* Page restored from bfcache — reload to get fresh state */
          window.location.reload();
        }
      });
    }

    async _restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const brand = params.get('pf_brand');
      const model = params.get('pf_model');
      const year = params.get('pf_year');
      if (!brand || !model || !year) return;

      this.state.brandName = brand;
      this.state.modelName = model;
      this.state.year = year;

      /* Resolve brandId from loaded brands */
      const found = this.cache.brands.find(b => b.name === brand);
      if (found) {
        this.state.brandId = String(found.id);
        this.cache.brandDataById.set(String(found.id), found);
      }

      if (this.elements.brandInput) this.elements.brandInput.value = brand;
      if (this.elements.modelInput) { this.elements.modelInput.value = model; this.elements.modelInput.disabled = false; }
      if (this.elements.yearInput) { this.elements.yearInput.value = year; this.elements.yearInput.disabled = false; }
      this.updateBreadcrumbs();

      /* Preload car image immediately */
      const imgUrl = this._getCarImageUrl(brand, model, parseInt(year));
      this._preloadImage(imgUrl);

      /* Populate model and year dropdown lists so user can change selection */
      if (this.state.brandId) {
        try {
          const brandData = await this.getBrandData(this.state.brandId);
          const models = Array.isArray(brandData?.models) ? brandData.models : [];
          this.renderModelList(models, this.state.brandId);

          const selectedModel = models.find(m => m.name === model);
          const years = Array.isArray(selectedModel?.years) ? selectedModel.years : [];
          this.renderYearList(years);
        } catch (e) { console.warn('Could not populate dropdowns from URL:', e); }
      }

      const filter = `brand_model_year:${brand}/${model}/${year}`;
      this.productState.currentFilter = filter;
      this.productState.currentPage = 0;
      this.productState.allHits = [];
      this.productState.activeFacets = {};
      this.searchProducts(filter, false);
    }

    _initPopstate() {
      window.addEventListener('popstate', () => {
        const params = new URLSearchParams(window.location.search);
        const brand = params.get('pf_brand');
        const model = params.get('pf_model');
        const year = params.get('pf_year');

        if (brand && model && year) {
          this.state.brandName = brand;
          this.state.modelName = model;
          this.state.year = year;
          /* Resolve brandId */
          const found = this.cache.brands.find(b => b.name === brand);
          if (found) {
            this.state.brandId = String(found.id);
            this.cache.brandDataById.set(String(found.id), found);
          }
          if (this.elements.brandInput) this.elements.brandInput.value = brand;
          if (this.elements.modelInput) { this.elements.modelInput.value = model; this.elements.modelInput.disabled = false; }
          if (this.elements.yearInput) { this.elements.yearInput.value = year; this.elements.yearInput.disabled = false; }
          this.updateBreadcrumbs();
          /* Populate dropdowns */
          if (this.state.brandId) {
            this.getBrandData(this.state.brandId).then(brandData => {
              const models = Array.isArray(brandData?.models) ? brandData.models : [];
              this.renderModelList(models, this.state.brandId);
              const selectedModel = models.find(m => m.name === model);
              const years = Array.isArray(selectedModel?.years) ? selectedModel.years : [];
              this.renderYearList(years);
            }).catch(() => {});
          }
          const filter = `brand_model_year:${brand}/${model}/${year}`;
          this.productState.currentFilter = filter;
          this.productState.currentPage = 0;
          this.productState.allHits = [];
          this.productState.activeFacets = {};
          this.searchProducts(filter, false);
        } else {
          /* Back to clean page — hide results, reset state */
          if (this.elements.resultsContainer) this.elements.resultsContainer.style.display = 'none';
          document.body.classList.remove('pf-results-active');
          document.body.classList.remove('pf-brand-grid-hidden');
          const brandGrid = document.querySelector('.pf-brand-grid-wrapper');
          if (brandGrid) { brandGrid.classList.remove('pf-brand-grid-hidden'); brandGrid.style.removeProperty('display'); }
          const sidebar = document.querySelector('.brands_card_side_bar');
          if (sidebar) sidebar.style.display = '';
          if (typeof displayBrands === 'function' && typeof BMYData !== 'undefined') displayBrands(BMYData);
          if (this.elements.carHeading) this.elements.carHeading.style.display = 'none';
          this.state.brandName = '';
          this.state.modelName = '';
          this.state.year = '';
          if (this.elements.brandInput) this.elements.brandInput.value = '';
          if (this.elements.modelInput) { this.elements.modelInput.value = ''; this.elements.modelInput.disabled = true; }
          if (this.elements.yearInput) { this.elements.yearInput.value = ''; this.elements.yearInput.disabled = true; }
          this.updateBreadcrumbs();
        }
      });
    }

    _preloadCarImages() {
      const cached = sessionStorage.getItem('pf_carImages');
      if (cached) {
        try {
          this.carImages = JSON.parse(cached);
          this._renderCarHero();
          return;
        } catch(e) {}
      }
      fetch('https://phpstack-1204751-4260962.cloudwaysapps.com/car-images.php')
        .then(r => r.json())
        .then(data => {
          this.carImages = Array.isArray(data) ? data : [];
          try { sessionStorage.setItem('pf_carImages', JSON.stringify(this.carImages)); } catch(e) {}
          this._renderCarHero();
        })
        .catch(() => { this.carImages = []; });
    }

    _getCarImageUrl(brand, model, year) {
      const y = parseInt(year) || 0;
      const imgs = this.carImages;
      // Priority 1: brand+model with matching year range
      const bmYear = imgs.find(i => i.key === brand + '/' + model && i.yf && i.yt && y >= i.yf && y <= i.yt);
      if (bmYear) return bmYear.url;
      // Priority 2: brand+model without year
      const bm = imgs.find(i => i.key === brand + '/' + model && !i.yf && !i.yt);
      if (bm) return bm.url;
      // Priority 3: brand with matching year range
      const bYear = imgs.find(i => i.key === brand && i.yf && i.yt && y >= i.yf && y <= i.yt);
      if (bYear) return bYear.url;
      // Priority 4: brand without year
      const b = imgs.find(i => i.key === brand && !i.yf && !i.yt);
      if (b) return b.url;
      return null;
    }

    /* Preload car images into browser cache */
    _preloadImage(url) {
      if (!url || this._preloadedUrls?.has(url)) return;
      if (!this._preloadedUrls) this._preloadedUrls = new Set();
      this._preloadedUrls.add(url);
      const img = new Image();
      img.src = url;
    }

    _preloadImagesForBrand(brandName) {
      if (!this.carImages?.length) return;
      this.carImages.forEach(i => {
        if (i.key === brandName || i.key.startsWith(brandName + '/')) {
          this._preloadImage(i.url);
        }
      });
    }

    _renderCarHero() {
      const container = this.elements.resultsContainer;
      if (!container) return;
      const b = this.state.brandName, m = this.state.modelName, y = this.state.year;
      if (!b || !m || !y) return;

      // Remove existing hero
      container.querySelector('.pf-car-hero')?.remove();

      // Instant lookup — no network request
      const imageUrl = this._getCarImageUrl(b, m, y);
      if (!imageUrl) return; // No image → keep current design with heading

      // Hide the separate car heading — the hero overlay has it
      if (this.elements.carHeading) this.elements.carHeading.style.display = 'none';

      const hero = document.createElement('div');
      hero.className = 'pf-car-hero';
      const isMobile = window.innerWidth < 768;

      hero.innerHTML = `
        <div class="pf-car-hero__image">
          <img src="${imageUrl}" alt="${b} ${m}" loading="eager">
          <div class="pf-car-hero__overlay">
            <span class="pf-car-hero__brand">${b.toUpperCase()}</span> <span class="pf-car-hero__model">${m}</span>
            <span class="pf-car-hero__sep">|</span>
            <span class="pf-car-hero__year">${y}</span>
          </div>
        </div>
        <div class="pf-car-hero__cta ${isMobile ? 'pf-car-hero__cta--collapsed' : ''}">
          ${isMobile ? '<button type="button" class="pf-car-hero__toggle">+ Vis mer</button>' : ''}
          <div class="pf-car-hero__cta-content">
            <p class="pf-car-hero__cta-title">Fant du ikke det du lette etter, eller ønsker du et tilbud?</p>
            <button type="button" class="button pf-car-hero__cta-btn" open-popup="#pf-hero-pristilbud" style="background:rgba(var(--color-accent-1));border-color:rgba(var(--color-accent-1));color:#fff;">
              Send meg et pristilbud!
            </button>
          </div>
        </div>
        <div class="form_popup_selection_form" id="pf-hero-pristilbud" popup-wrapper="">
          <div class="form_overlay">&nbsp;</div>
          <div class="form_popup_data_selection">
            <div class="form-container_selection">
              <div class="form-header_Selection">
                <button type="button" class="button button-tertiary button-drawer-header-dismiss dropdown-modal-toggle close-button" aria-label="Lukk" style="margin-right:1rem;">×</button>
                <h2>Pristilbud</h2>
              </div>
              <div class="form-content_selction_form">
                <form method="post" action="/contact#pf_hero_ContactForm" id="pf_hero_ContactForm" accept-charset="UTF-8" class="isolate" novalidate="novalidate">
                  <input type="hidden" name="form_type" value="contact">
                  <input type="hidden" name="utf8" value="✓">
                  <input type="hidden" name="contact[car]" value="${b} ${m} ${y}">
                  <input type="hidden" name="contact[regnr_lookup]" value="${this.state.regNumber || ''}">
                  <div class="form_main_fields">
                    <input type="text" placeholder="Navn" name="contact[name]" required>
                    <input type="email" placeholder="E-post" name="contact[email]" required>
                    <input type="tel" placeholder="Telefon" name="contact[phone]" pattern="[0-9\\-]*" autocomplete="tel" required>
                    <input type="text" placeholder="Regnr" name="contact[Regnr]" value="${this.state.regNumber || ''}">
                    <input type="text" placeholder="Bilmerke" name="contact[Bilmerke]" value="${b}">
                    <input type="text" placeholder="Modell" name="contact[Modell]" value="${m}">
                    <input type="text" placeholder="Årsmodell" name="contact[Årsmodell]" value="${y}">
                    <textarea placeholder="Kommentar" name="contact[Message]"></textarea>
                    <button type="submit" class="button button-primary submit-form_request">Send meg et pristilbud!</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>`;

      // Insert hero after car heading, before header
      const header = container.querySelector('.partfinder__results-header');
      const carHeading = container.querySelector('[data-results-car-heading]');
      if (header) container.insertBefore(hero, header);
      else if (carHeading) carHeading.after(hero);
      else container.prepend(hero);

      // Wire popup
      const ctaBtn = hero.querySelector('[open-popup]');
      const popup = hero.querySelector('#pf-hero-pristilbud');
      if (ctaBtn && popup) {
        ctaBtn.addEventListener('click', () => popup.classList.add('active'));
        popup.querySelector('.form_overlay')?.addEventListener('click', () => popup.classList.remove('active'));
        popup.querySelector('.close-button')?.addEventListener('click', () => popup.classList.remove('active'));
      }

      // Wire mobile toggle — Web Animations API for garantert iOS Safari-kompatibilitet
      const toggle = hero.querySelector('.pf-car-hero__toggle');
      if (toggle) {
        const cta = hero.querySelector('.pf-car-hero__cta');
        const content = hero.querySelector('.pf-car-hero__cta-content');

        if (cta && content) {
          // Initial state — hvis starter collapsed, gjør content usynlig
          const startsCollapsed = cta.classList.contains('pf-car-hero__cta--collapsed');
          if (startsCollapsed) {
            content.style.height = '0px';
            content.style.opacity = '0';
            content.style.overflow = 'hidden';
          }

          let isAnimating = false;

          toggle.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            if (isAnimating) return;

            const isCollapsed = cta.classList.contains('pf-car-hero__cta--collapsed');

            // Mål den ekte høyden ved midlertidig å vise innholdet skjult
            function measureHeight() {
              const saved = {
                height: content.style.height,
                opacity: content.style.opacity,
                overflow: content.style.overflow,
                visibility: content.style.visibility,
                position: content.style.position
              };
              content.style.visibility = 'hidden';
              content.style.position = 'absolute';
              content.style.height = 'auto';
              content.style.overflow = 'visible';
              content.style.opacity = '1';
              const h = content.scrollHeight;
              // Restore before animating
              content.style.position = saved.position;
              content.style.visibility = '';
              content.style.height = saved.height;
              content.style.opacity = saved.opacity;
              content.style.overflow = saved.overflow;
              return h;
            }

            isAnimating = true;

            if (isCollapsed) {
              // === EXPAND ===
              cta.classList.remove('pf-car-hero__cta--collapsed');
              const targetHeight = measureHeight();
              content.style.overflow = 'hidden';
              content.style.height = '0px';
              content.style.opacity = '0';

              const anim = content.animate(
                [
                  { height: '0px', opacity: 0 },
                  { height: targetHeight + 'px', opacity: 1 }
                ],
                {
                  duration: 320,
                  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                  fill: 'forwards'
                }
              );
              anim.onfinish = function() {
                content.style.height = 'auto';
                content.style.opacity = '1';
                content.style.overflow = '';
                isAnimating = false;
              };
              toggle.textContent = '− Vis mindre';
            } else {
              // === COLLAPSE ===
              const currentHeight = content.offsetHeight || content.scrollHeight;
              content.style.overflow = 'hidden';
              content.style.height = currentHeight + 'px';
              content.style.opacity = '1';

              const anim = content.animate(
                [
                  { height: currentHeight + 'px', opacity: 1 },
                  { height: '0px', opacity: 0 }
                ],
                {
                  duration: 300,
                  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                  fill: 'forwards'
                }
              );
              anim.onfinish = function() {
                content.style.height = '0px';
                content.style.opacity = '0';
                isAnimating = false;
              };
              cta.classList.add('pf-car-hero__cta--collapsed');
              toggle.textContent = '+ Vis mer';
            }
          }, { passive: false });
        }
      }
    }

    _buildInlineSearch() {
      const sidebar = this.elements.filtersSidebar;
      if (!sidebar || sidebar.querySelector('.pf-inline-search')) return;

      const searchIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>';
      const flagSvg = '<svg viewBox="0 0 32 24" xmlns="http://www.w3.org/2000/svg"><rect width="32" height="24" fill="#BA0C2F"/><rect x="6" width="4" height="24" fill="#fff"/><rect y="10" width="32" height="4" fill="#fff"/><rect x="7" width="2" height="24" fill="#00205B"/><rect y="11" width="32" height="2" fill="#00205B"/></svg>';

      const bar = document.createElement('div');
      bar.className = 'pf-inline-search';
      bar.innerHTML = `
        <div class="pf-inline-search__plate">
          <span class="pf-inline-search__plate-prefix">${flagSvg}<span style="font-size:7px;font-weight:700;">N</span></span>
          <input type="text" class="pf-inline-search__plate-input" placeholder="AB 12345" data-pf-inline-reg>
          <button type="button" class="pf-inline-search__plate-btn" data-pf-inline-reg-btn>${searchIcon}</button>
        </div>
        <div class="pf-inline-search__sep"></div>
        <select class="pf-inline-search__select" data-pf-inline-brand></select>
        <select class="pf-inline-search__select" data-pf-inline-model><option value="">Modell</option></select>
        <select class="pf-inline-search__select" data-pf-inline-year><option value="">År</option></select>
        <button type="button" class="pf-inline-search__submit" data-pf-inline-submit>${searchIcon}</button>
      `;

      /* Populate brand dropdown */
      const brandSelect = bar.querySelector('[data-pf-inline-brand]');
      const brandOpt = document.createElement('option');
      brandOpt.value = '';
      brandOpt.textContent = 'Bilmerke';
      brandSelect.appendChild(brandOpt);
      this.cache.brands.sort((a, b) => a.name.localeCompare(b.name)).forEach(b => {
        const opt = document.createElement('option');
        opt.value = b.id;
        opt.textContent = b.name;
        if (b.name === this.state.brandName) opt.selected = true;
        brandSelect.appendChild(opt);
      });

      /* Brand change → load models */
      brandSelect.addEventListener('change', async () => {
        const modelSelect = bar.querySelector('[data-pf-inline-model]');
        const yearSelect = bar.querySelector('[data-pf-inline-year]');
        modelSelect.innerHTML = '<option value="">Modell</option>';
        yearSelect.innerHTML = '<option value="">År</option>';
        const brandId = brandSelect.value;
        if (!brandId) return;
        this.state.brandId = brandId;
        this.state.brandName = brandSelect.options[brandSelect.selectedIndex].text;
        if (this.elements.brandInput) this.elements.brandInput.value = this.state.brandName;

        const data = await this.getBrandData(brandId);
        (data?.models || []).forEach(m => {
          const opt = document.createElement('option');
          opt.value = m.name;
          opt.textContent = m.name;
          if (m.name === this.state.modelName) opt.selected = true;
          modelSelect.appendChild(opt);
        });
      });

      /* Model change → load years */
      bar.querySelector('[data-pf-inline-model]').addEventListener('change', async () => {
        const modelSelect = bar.querySelector('[data-pf-inline-model]');
        const yearSelect = bar.querySelector('[data-pf-inline-year]');
        yearSelect.innerHTML = '<option value="">År</option>';
        const modelName = modelSelect.value;
        if (!modelName) return;
        this.state.modelName = modelName;
        if (this.elements.modelInput) this.elements.modelInput.value = modelName;

        const data = await this.getBrandData(this.state.brandId);
        const model = data?.models?.find(m => m.name === modelName);
        (model?.years || []).forEach(y => {
          const opt = document.createElement('option');
          opt.value = y;
          opt.textContent = y;
          if (String(y) === String(this.state.year)) opt.selected = true;
          yearSelect.appendChild(opt);
        });
      });

      /* Year change → auto-search */
      bar.querySelector('[data-pf-inline-year]').addEventListener('change', () => {
        const year = bar.querySelector('[data-pf-inline-year]').value;
        if (!year) return;
        this.state.year = year;
        if (this.elements.yearInput) this.elements.yearInput.value = year;
        this.handleManualSearch();
      });

      /* Submit button */
      bar.querySelector('[data-pf-inline-submit]').addEventListener('click', () => {
        if (this.state.brandName && this.state.modelName && bar.querySelector('[data-pf-inline-year]').value) {
          this.state.year = bar.querySelector('[data-pf-inline-year]').value;
          this.handleManualSearch();
        }
      });

      /* Regnr search */
      bar.querySelector('[data-pf-inline-reg-btn]').addEventListener('click', () => {
        const val = bar.querySelector('[data-pf-inline-reg]').value;
        if (val) {
          this.elements.registrationInput.value = val;
          this.handleRegistrationSearch();
        }
      });
      bar.querySelector('[data-pf-inline-reg]').addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const val = bar.querySelector('[data-pf-inline-reg]').value;
          if (val) { this.elements.registrationInput.value = val; this.handleRegistrationSearch(); }
        }
      });
      bar.querySelector('[data-pf-inline-reg]').addEventListener('input', function() {
        const pos = this.selectionStart;
        this.value = this.value.toUpperCase();
        this.setSelectionRange(pos, pos);
      });

      /* Insert at top of sidebar (before first filter group) */
      const firstChild = sidebar.querySelector('.pf-filter-drawer-body') || sidebar.firstChild;
      if (firstChild) sidebar.insertBefore(bar, firstChild);
      else sidebar.appendChild(bar);
    }

    _initViewToggle() {
      this.root.querySelectorAll('[data-pf-view]').forEach(btn => {
        btn.addEventListener('click', () => {
          const mode = btn.dataset.pfView;
          const grid = this.elements.resultsGrid;
          if (!grid) return;
          this.root.querySelectorAll('[data-pf-view]').forEach(b => b.classList.remove('pf-view-btn--active'));
          btn.classList.add('pf-view-btn--active');
          grid.classList.toggle('pf-grid--list', mode === 'list');
          grid.classList.toggle('pf-grid--cards', mode === 'grid');
          this.productState.viewMode = mode;
        });
      });
    }

    _initPlateInput() {
      const input = this.elements.registrationInput;
      if (!input) return;
      if (!input.getAttribute('placeholder')) input.setAttribute('placeholder', 'AB 12345');
      input.addEventListener('input', () => {
        const pos = input.selectionStart;
        input.value = input.value.toUpperCase();
        input.setSelectionRange(pos, pos);
      });
    }

    _injectCustomSortOptions() {
      const select = this.elements.resultsSort;
      if (!select || !this.customSorts.length) return;

      /* Add separator */
      const sep = document.createElement('option');
      sep.disabled = true;
      sep.textContent = '── Egendefinert ──';
      select.insertBefore(sep, select.firstChild);

      /* Add custom sorts at top (reversed so first appears first) */
      this.customSorts.slice().reverse().forEach(cs => {
        const opt = document.createElement('option');
        opt.value = 'custom_' + cs.sort_key;
        opt.textContent = cs.name;
        select.insertBefore(opt, select.firstChild);
      });

      /* Set default selection */
      select.value = this.productState.currentSort;
    }

    _applyCustomTagSort(hits, sortTags) {
      const priorityMap = {};
      sortTags.forEach(t => { priorityMap[t.tag.toLowerCase()] = t.priority; });
      const maxPriority = sortTags.length + 1;

      return hits.slice().sort((a, b) => {
        const aTags = Array.isArray(a.tags) ? a.tags : (typeof a.tags === 'string' ? a.tags.split(', ') : []);
        const bTags = Array.isArray(b.tags) ? b.tags : (typeof b.tags === 'string' ? b.tags.split(', ') : []);

        let aPri = maxPriority, bPri = maxPriority;
        aTags.forEach(tag => {
          const p = priorityMap[tag.toLowerCase()];
          if (p !== undefined && p < aPri) aPri = p;
        });
        bTags.forEach(tag => {
          const p = priorityMap[tag.toLowerCase()];
          if (p !== undefined && p < bPri) bPri = p;
        });

        return aPri - bPri;
      });
    }

    _initAria() {
      const lists = [
        { input: this.elements.brandInput, list: this.elements.brandList, id: 'partfinder-brand-list' },
        { input: this.elements.modelInput, list: this.elements.modelList, id: 'partfinder-model-list' },
        { input: this.elements.yearInput, list: this.elements.yearList, id: 'partfinder-year-list' }
      ];
      lists.forEach(({ input, list, id }) => {
        if (input) {
          input.setAttribute('role', 'combobox');
          input.setAttribute('aria-autocomplete', 'list');
          input.setAttribute('aria-expanded', 'false');
          input.setAttribute('aria-controls', id);
          input.setAttribute('aria-haspopup', 'listbox');
        }
        if (list) { list.setAttribute('role', 'listbox'); list.id = id; }
      });
      const toast = this.elements.toast;
      if (toast) {
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        toast.setAttribute('aria-atomic', 'true');
      }
    }

    bindEvents() {
      this.elements.mobileOpen?.addEventListener('click', () => this.openMobile());
      this.elements.mobileClose?.addEventListener('click', () => this.closeMobile());
      this.elements.registrationSubmit?.addEventListener('click', () => this.handleRegistrationSearch());
      this.elements.manualSubmit?.addEventListener('click', () => this.handleManualSearch());
      this.elements.registrationInput?.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.handleRegistrationSearch(); });

      this.elements.brandInput?.addEventListener('focus', () => { if (!this._isTransitioning) this.openField('brand'); });
      this.elements.modelInput?.addEventListener('focus', () => {
        if (this._isTransitioning) return;
        if (!this.state.brandName) { this.elements.brandInput?.focus(); this.showToast('Velg bilmerke først.'); return; }
        this.openField('model');
      });
      this.elements.yearInput?.addEventListener('focus', () => {
        if (this._isTransitioning) return;
        if (!this.state.brandName) { this.elements.brandInput?.focus(); this.showToast('Velg bilmerke først.'); return; }
        if (!this.state.modelName) { this.elements.modelInput?.focus(); this.showToast('Velg modell først.'); return; }
        this.openField('year');
      });

      this.elements.brandInput?.addEventListener('input', () => { this.openField('brand'); this._debouncedFilterBrand(); });
      this.elements.modelInput?.addEventListener('input', () => {
        if (!this.state.brandName) { this.elements.modelInput.value = ''; this.elements.brandInput?.focus(); this.showToast('Velg bilmerke først.'); return; }
        this.openField('model'); this._debouncedFilterModel();
      });
      this.elements.yearInput?.addEventListener('input', () => {
        if (!this.state.modelName) {
          this.elements.yearInput.value = '';
          if (!this.state.brandName) { this.elements.brandInput?.focus(); this.showToast('Velg bilmerke først.'); }
          else { this.elements.modelInput?.focus(); this.showToast('Velg modell først.'); }
          return;
        }
        this.openField('year'); this._debouncedFilterYear();
      });

      [this.elements.brandInput, this.elements.modelInput, this.elements.yearInput].forEach((input) => {
        input?.addEventListener('keydown', (e) => this.handleListKeyboard(e, input));
      });

      this.elements.brandList?.addEventListener('mousedown', (e) => { const item = e.target.closest('li'); if (item) { e.preventDefault(); this.selectBrand(item); } });
      this.elements.modelList?.addEventListener('mousedown', (e) => {
        const item = e.target.closest('li'); if (!item) return;
        if (!this.state.brandName) { e.preventDefault(); this.elements.brandInput?.focus(); this.showToast('Velg bilmerke først.'); return; }
        e.preventDefault(); this.selectModel(item);
      });
      this.elements.yearList?.addEventListener('mousedown', (e) => {
        const item = e.target.closest('li'); if (!item) return;
        if (!this.state.brandName) { e.preventDefault(); this.elements.brandInput?.focus(); this.showToast('Velg bilmerke først.'); return; }
        if (!this.state.modelName) { e.preventDefault(); this.elements.modelInput?.focus(); this.showToast('Velg modell først.'); return; }
        e.preventDefault(); this.selectYear(item);
      });

      this.elements.focusButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const input = this.root.querySelector(button.dataset.focusInput);
          if (!input) return;
          if (input === this.elements.modelInput && !this.state.brandName) { this.elements.brandInput?.focus(); this.showToast('Velg bilmerke først.'); return; }
          if (input === this.elements.yearInput) {
            if (!this.state.brandName) { this.elements.brandInput?.focus(); this.showToast('Velg bilmerke først.'); return; }
            if (!this.state.modelName) { this.elements.modelInput?.focus(); this.showToast('Velg modell først.'); return; }
          }
          input.focus();
        });
      });

      this.elements.backButtons.forEach((button) => { button.addEventListener('click', () => this.goBack(button.dataset.backTo)); });
      this.elements.spotlight?.addEventListener('mousedown', () => { if (!this._isTransitioning) this.closeAllDropdowns(); });
      document.addEventListener('mousedown', (e) => { if (!this._isTransitioning && !this.root.contains(e.target)) this.closeAllDropdowns(); });

      /* Product results events */
      this.elements.resultsSort?.addEventListener('change', () => {
        this.productState.currentSort = this.elements.resultsSort.value;
        this.productState.currentPage = 0;
        this.productState.allHits = [];
        this.searchProducts(this.productState.currentFilter, false);
      });
      this.elements.loadMoreBtn?.addEventListener('click', () => {
        this.productState.currentPage++;
        this.searchProducts(this.productState.currentFilter, true);
      });

      /* Mobile filter drawer toggle */
      this.elements.filterToggleBtn?.addEventListener('click', () => this._openFilterDrawer());
    }

    _openFilterDrawer() {
      const sidebar = this.elements.filtersSidebar;
      if (!sidebar) return;
      /* Move sidebar to body to escape stacking context */
      if (sidebar.parentElement !== document.body) {
        this._filterOriginalParent = sidebar.parentElement;
        this._filterOriginalNext = sidebar.nextSibling;
      }
      /* Create overlay */
      if (!this._filterOverlay) {
        this._filterOverlay = document.createElement('div');
        this._filterOverlay.className = 'pf-filter-overlay';
        this._filterOverlay.addEventListener('click', () => this._closeFilterDrawer());
      }
      /* Append overlay first, then sidebar (without is-open = off-screen right) */
      document.body.appendChild(this._filterOverlay);
      sidebar.classList.remove('is-open');
      document.body.appendChild(sidebar);
      document.body.style.overflow = 'hidden';
      /* Wait two frames so browser renders off-screen state, then slide in */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          sidebar.classList.add('is-open');
        });
      });
    }

    _closeFilterDrawer() {
      const sidebar = this.elements.filtersSidebar;
      if (!sidebar) return;
      sidebar.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this._filterOverlay && this._filterOverlay.parentNode) {
        this._filterOverlay.parentNode.removeChild(this._filterOverlay);
      }
      /* Move sidebar back to original position */
      if (this._filterOriginalParent) {
        if (this._filterOriginalNext) {
          this._filterOriginalParent.insertBefore(sidebar, this._filterOriginalNext);
        } else {
          this._filterOriginalParent.appendChild(sidebar);
        }
      }
    }

    /* ══════════ DATA LOADING ══════════ */

    async loadBrands() {
      this._showListLoading(this.elements.brandList);
      try {
        const response = await this.index.search('', this.requestData);
        this.cache.brands = Array.isArray(response.hits) ? response.hits : [];
        this.cache.brands.forEach((brand) => { if (brand?.id) this.cache.brandDataById.set(String(brand.id), brand); });
        this.renderBrandList(this.cache.brands);
      } catch (error) {
        console.error('Error loading brands:', error);
        this._hideListLoading(this.elements.brandList);
        this.showToast('Klarte ikke å laste bilmerker.');
      }
    }

    /* ══════════ LIST RENDERING ══════════ */

    renderBrandList(brands) {
      const list = this.elements.brandList;
      if (!list) return;
      this._clearList(list);
      const popularNames = ['Audi', 'BMW', 'Ford', 'Mercedes', 'Tesla', 'Toyota', 'Volvo', 'Volkswagen'];
      const popular = brands.filter(b => popularNames.includes(b.name)).sort((a, b) => a.name.localeCompare(b.name));
      const others = brands.filter(b => !popularNames.includes(b.name)).sort((a, b) => a.name.localeCompare(b.name));

      const ph = document.createElement('h3'); ph.textContent = 'Populære Bilmerker';
      const ah = document.createElement('h3'); ah.textContent = 'Alle Bilmerker';
      const pg = document.createElement('div'); pg.className = 'partfinder__popular-group';
      const ag = document.createElement('div');

      popular.forEach(b => { const n = this._sanitize(b.name), id = this._sanitize(String(b.id||'')); pg.appendChild(this.createListItem(n, id, { brandId: id })); });
      others.forEach(b => { const n = this._sanitize(b.name), id = this._sanitize(String(b.id||'')); ag.appendChild(this.createListItem(n, id, { brandId: id })); });

      list.appendChild(ph); list.appendChild(pg); list.appendChild(ah); list.appendChild(ag);
    }

    renderModelList(models, brandId) {
      const list = this.elements.modelList;
      if (!list) return;
      this._clearList(list); this._hideListLoading(list);
      (Array.isArray(models) ? models : []).forEach(m => {
        list.appendChild(this.createListItem(this._sanitize(m.name), this._sanitize(String(m?.id||m?.name||'')), { brandId: this._sanitize(String(brandId||'')) }));
      });
    }

    renderYearList(years) {
      const list = this.elements.yearList;
      if (!list) return;
      this._clearList(list); this._hideListLoading(list);
      (Array.isArray(years) ? years : []).forEach(y => { list.appendChild(this.createListItem(this._sanitize(String(y)), this._sanitize(String(y)))); });
    }

    createListItem(text, value, extraDataset = {}) {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.id = 'pf-option-' + Math.random().toString(36).slice(2, 9);
      li.dataset.content = text; li.dataset.value = value; li.textContent = text;
      Object.entries(extraDataset).forEach(([k, v]) => { li.dataset[k] = v; });
      return li;
    }

    _clearList(list) { if (!list) return; while (list.firstChild) list.removeChild(list.firstChild); }

    _showListLoading(list) {
      if (!list) return; this._clearList(list);
      const loader = document.createElement('div'); loader.className = 'partfinder__list-loader'; loader.setAttribute('aria-label', 'Laster...');
      for (let i = 0; i < 4; i++) { const s = document.createElement('div'); s.className = 'partfinder__skeleton-row'; loader.appendChild(s); }
      list.appendChild(loader);
    }

    _hideListLoading(list) { if (!list) return; const l = list.querySelector('.partfinder__list-loader'); if (l) l.remove(); }

    /* ══════════ FILTERING & FIELD STATE ══════════ */

    filterVisibleList(input, list) {
      if (!input || !list) return;
      const q = input.value.trim().toLowerCase();
      list.querySelectorAll('li').forEach(item => { item.style.display = item.textContent.toLowerCase().includes(q) ? '' : 'none'; });
      this.activeIndex = -1; this.activeList = list;
      this.clearFocusedItems(list); this._setActiveDescendant(input, null);
    }

    openField(stepName) {
      if (window.innerWidth <= 1024) return;
      this.closeAllDropdowns();
      const target = this.root.querySelector(`.partfinder__field-wrap[data-step="${stepName}"]`);
      if (!target) return;
      target.classList.add('is-open');
      this.elements.bar?.classList.add('is-spotlight-active');
      const map = { brand: this.elements.brandInput, model: this.elements.modelInput, year: this.elements.yearInput };
      this._setExpanded(map[stepName], true);
    }

    closeAllDropdowns() {
      this.elements.fieldWraps.forEach(w => w.classList.remove('is-open'));
      this.elements.bar?.classList.remove('is-spotlight-active');
      this.activeList = null; this.activeIndex = -1;
      [this.elements.brandInput, this.elements.modelInput, this.elements.yearInput].forEach(input => {
        this._setExpanded(input, false); this._setActiveDescendant(input, null);
      });
    }

    /* ══════════ SELECTION HANDLERS ══════════ */

    async selectBrand(item) {
      const brandId = String(item.dataset.brandId || item.dataset.value || '');
      const brandName = item.dataset.content || '';
      this._isTransitioning = true;
      this.state.brandId = brandId; this.state.brandName = brandName;
      this.state.modelId = ''; this.state.modelName = ''; this.state.year = '';
      /* Preload all images for this brand while user picks model */
      this._preloadImagesForBrand(brandName);
      if (this.elements.brandInput) this.elements.brandInput.value = brandName;
      if (this.elements.modelInput) this.elements.modelInput.value = '';
      if (this.elements.yearInput) this.elements.yearInput.value = '';
      if (this.elements.modelInput) this.elements.modelInput.disabled = false;
      if (this.elements.yearInput) this.elements.yearInput.disabled = true;
      this._clearList(this.elements.modelList); this._clearList(this.elements.yearList);
      this.updateBreadcrumbs(); this.setMobileTitle('Velg Modell');
      this._showListLoading(this.elements.modelList);
      try {
        const brandData = await this.getBrandData(brandId);
        const models = Array.isArray(brandData?.models) ? brandData.models : [];
        this.renderModelList(models, brandId);
        if (models.length === 0) this.showToast('Ingen modeller funnet for dette merket.');
      } catch (error) {
        console.error('Error loading models:', error);
        this._hideListLoading(this.elements.modelList);
        this.showToast('Klarte ikke å laste modeller. Prøv igjen.');
        this._isTransitioning = false; return;
      }
      if (window.innerWidth <= 1024) this.showMobileStep('model');
      else { this.openField('model'); this.elements.modelInput?.focus(); }
      setTimeout(() => { this._isTransitioning = false; }, 200);
    }

    async selectModel(item) {
      const brandId = String(item.dataset.brandId || this.state.brandId || '');
      const modelName = item.dataset.content || '';
      this._isTransitioning = true;
      this.state.modelId = String(item.dataset.value || ''); this.state.modelName = modelName; this.state.year = '';
      if (this.elements.modelInput) this.elements.modelInput.value = modelName;
      if (this.elements.yearInput) this.elements.yearInput.value = '';
      if (this.elements.yearInput) this.elements.yearInput.disabled = false;
      this._clearList(this.elements.yearList);
      this.updateBreadcrumbs(); this.setMobileTitle('Velg Årsmodell');
      this._showListLoading(this.elements.yearList);
      try {
        const brandData = await this.getBrandData(brandId);
        const selectedModel = Array.isArray(brandData?.models) ? brandData.models.find(m => m.name === modelName) : null;
        const years = Array.isArray(selectedModel?.years) ? selectedModel.years : [];
        this.renderYearList(years);
        if (years.length === 0) this.showToast('Ingen årsmodeller funnet for denne modellen.');
      } catch (error) {
        console.error('Error loading years:', error);
        this._hideListLoading(this.elements.yearList);
        this.showToast('Klarte ikke å laste årsmodeller. Prøv igjen.');
        this._isTransitioning = false; return;
      }
      if (window.innerWidth <= 1024) this.showMobileStep('year');
      else { this.openField('year'); this.elements.yearInput?.focus(); }
      setTimeout(() => { this._isTransitioning = false; }, 200);
    }

    selectYear(item) {
      const year = item.dataset.content || '';
      this.state.year = year;
      if (this.elements.yearInput) this.elements.yearInput.value = year;
      this.updateBreadcrumbs();
      if (window.innerWidth <= 1024) this.closeMobile();
      else this.closeAllDropdowns();
      this.handleManualSearch();
    }

    /* ══════════ DATA FETCHING ══════════ */

    async getBrandData(brandId) {
      const nid = String(brandId || '');
      if (this.cache.brandDataById.has(nid)) return this.cache.brandDataById.get(nid);
      try {
        const response = await this.index.search('', { ...this.requestData, query: '', filters: `id=${nid}` });
        const hit = response.hits?.[0] || null;
        if (hit?.id) this.cache.brandDataById.set(String(hit.id), hit);
        return hit;
      } catch (error) { console.error('Error loading brand data:', error); throw error; }
    }

    /* ══════════ SEARCH HANDLERS — INSTANT ══════════ */

    handleManualSearch() {
      const { brandName, modelName, year } = this.state;
      if (!brandName) { this.elements.brandInput?.focus(); this.openField('brand'); return; }
      if (!modelName) { this.elements.modelInput?.focus(); this.openField('model'); return; }
      if (!year) { this.elements.yearInput?.focus(); this.openField('year'); return; }

      this.persistSelectedCar({ brand_id: this.state.brandId, brand_name: brandName, model_id: this.state.modelId, model_name: modelName, year: year });
      localStorage.setItem('Selected-model', modelName);
      localStorage.setItem('Selected-brand', brandName);
      localStorage.setItem('Selected-year', year);

      const filter = `brand_model_year:${brandName}/${modelName}/${year}`;
      const isFirstSearch = !this.productState.currentFilter;
      this.productState.currentFilter = filter;
      this.productState.currentPage = 0;
      this.productState.allHits = [];
      this.productState.activeFacets = {};

      const filterUrl = this.buildFilterRedirectUrl(brandName, modelName, year);
      if (isFirstSearch) {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({ partfinder: false }, '', cleanUrl);
      }
      window.history.pushState({ partfinder: true }, '', filterUrl);

      this.searchProducts(filter, false);
    }

    async handleRegistrationSearch() {
      const raw = this.elements.registrationInput?.value || '';
      const regNumber = this.normalizeRegistration(raw);
      if (!regNumber) { this.showToast('Skriv inn registreringsnummer.'); return; }
      this.state.regNumber = regNumber;
      this.setRegistrationLoading(true);

      const cacheKey = `regLookup:${regNumber}`;
      const cached = this.getCachedRegistration(cacheKey);
      if (cached) { this.applyRegistrationData(cached, regNumber); this.setRegistrationLoading(false); return; }

      const apiUrl = `${this.config.registrationApi}?model_no=${encodeURIComponent(regNumber)}`;
      let timeoutId;
      try {
        const controller = new AbortController();
        timeoutId = window.setTimeout(() => controller.abort(), 4500);
        const response = await fetch(apiUrl, { method: 'GET', signal: controller.signal, headers: { Accept: 'application/json' } });
        window.clearTimeout(timeoutId);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        this.cacheRegistration(cacheKey, data);
        this.applyRegistrationData(data, regNumber);
      } catch (error) {
        window.clearTimeout(timeoutId);
        console.error('Registration search failed:', error);
        this.showToast(error.name === 'AbortError' ? 'Oppslaget tok for lang tid. Prøv igjen.' : 'Det oppstod en feil. Prøv igjen.');
      } finally { this.setRegistrationLoading(false); }
    }

    setRegistrationLoading(isLoading) {
      const btn = this.elements.registrationSubmit;
      if (!btn) return;
      btn.disabled = isLoading;
      btn.classList.toggle('is-loading', isLoading);
      btn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (isLoading) { if (!btn.dataset.originalHtml) btn.dataset.originalHtml = btn.innerHTML; btn.innerHTML = '<span class="partfinder__spinner"></span>'; }
      else if (btn.dataset.originalHtml) btn.innerHTML = btn.dataset.originalHtml;
    }

    /* ══════════ INSTANT PRODUCT SEARCH ══════════ */

    async searchProducts(filter, append) {
      if (!this.productClient) {
        window.location.href = this.buildFilterRedirectUrl(this.state.brandName, this.state.modelName, this.state.year);
        return;
      }

      const container = this.elements.resultsContainer;
      const grid = this.elements.resultsGrid;
      if (!container || !grid) return;

      container.style.display = '';
      document.body.classList.add('pf-results-active');
      /* Hide brand grid on dedicated filter page */
      if (document.querySelector('.pf-brand-grid-wrapper')) {
        document.body.classList.add('pf-brand-grid-hidden');
        document.querySelector('.pf-brand-grid-wrapper')?.classList.add('pf-brand-grid-hidden');
      }
      if (!append) grid.innerHTML = '';

      try {
        const sortKey = this.productState.currentSort;
        const isCustomSort = sortKey.startsWith('custom_');
        let customSortConfig = null;
        let algoliaIndex;

        if (isCustomSort) {
          const customKey = sortKey.replace('custom_', '');
          customSortConfig = this.customSorts.find(cs => cs.sort_key === customKey);
          const fallback = customSortConfig?.fallback_sort || 'best_seller';
          algoliaIndex = this.sortIndices[fallback] || this.config.productIndex;
        } else {
          algoliaIndex = this.sortIndices[sortKey] || this.config.productIndex;
        }
        const searchIndex = this.productClient.initIndex(algoliaIndex);

        const facetFilters = [[filter]];
        Object.entries(this.productState.activeFacets).forEach(([facetName, values]) => {
          if (values.length > 0) {
            facetFilters.push(values.map(v => `${facetName}:${v}`));
          }
        });

        /* Custom sort: fetch ALL hits, sort by tag priority, paginate locally */
        if (isCustomSort && customSortConfig && customSortConfig.tags && !append) {
          const allResults = await searchIndex.search('', {
            facetFilters: facetFilters,
            hitsPerPage: 1000,
            page: 0,
            facets: ['categories', 'tags', 'vendor']
          });
          /* Filter out products with 0 price and remove duplicates */
          const seen = new Set();
          const cleanHits = allResults.hits.filter(h => {
            const key = h.handle || h.objectID;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            if (h.sku && seen.has('sku_' + h.sku)) return false;
            if (h.sku) seen.add('sku_' + h.sku);
            const price = parseFloat(h.price || 0);
            return price > 0;
          });
          this.productState.totalHits = cleanHits.length;
          const sortedAll = this._applyCustomTagSort(cleanHits, customSortConfig.tags);
          const pageSize = this.productState.hitsPerPage;
          const start = this.productState.currentPage * pageSize;
          this.productState.allHits = sortedAll.slice(start, start + pageSize);
          this.productState._customSortedAll = sortedAll; // cache for pagination
          var results = allResults; // for facets below
        } else if (isCustomSort && customSortConfig && customSortConfig.tags && append) {
          /* Paginating within custom-sorted results */
          const sortedAll = this.productState._customSortedAll || [];
          const pageSize = this.productState.hitsPerPage;
          const start = this.productState.currentPage * pageSize;
          const pageHits = sortedAll.slice(start, start + pageSize);
          this.productState.allHits = this.productState.allHits.concat(pageHits);
          this.renderProducts(append);
          /* Show/hide load more button */
          const totalLoaded = this.productState.allHits.length;
          if (this.elements.resultsPagination) {
            this.elements.resultsPagination.style.display = totalLoaded < this.productState.totalHits ? '' : 'none';
          }
          return; // facets already rendered
        } else {
          var results = await searchIndex.search('', {
            facetFilters: facetFilters,
            hitsPerPage: this.productState.hitsPerPage,
            page: this.productState.currentPage,
            facets: ['categories', 'tags', 'vendor']
          });
          this.productState.totalHits = results.nbHits;
          if (append) this.productState.allHits = this.productState.allHits.concat(results.hits);
          else this.productState.allHits = results.hits;
        }

        this.renderProducts(append);
        this.renderFacets(results.facets || {});

        /* Hide sidebar + header controls when no results */
        const noResults = this.productState.totalHits === 0;
        if (this.elements.filtersSidebar) this.elements.filtersSidebar.style.display = noResults ? 'none' : '';
        const headerRow = container.querySelector('.partfinder__results-header');
        if (headerRow) headerRow.style.display = noResults ? 'none' : '';

        if (this.elements.resultsCount) this.elements.resultsCount.textContent = `${this.productState.totalHits} Produkter`;

        /* Car heading with optional regnr */
        /* Add "back to brands" button on dedicated filter page */
        if (!append && document.querySelector('.pf-brand-grid-wrapper')) {
          let backBtn = this.elements.resultsContainer.querySelector('.pf-brand-grid-back');
          if (!backBtn) {
            backBtn = document.createElement('a');
            backBtn.className = 'pf-brand-grid-back';
            backBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg> Tilbake til bilmerker';
            backBtn.addEventListener('click', (e) => {
              e.preventDefault();
              document.body.classList.remove('pf-brand-grid-hidden');
              const brandGrid = document.querySelector('.pf-brand-grid-wrapper');
              if (brandGrid) {
                brandGrid.classList.remove('pf-brand-grid-hidden');
                brandGrid.style.removeProperty('display');
              }
              /* Re-render brands since card-brands innerHTML was replaced */
              if (typeof displayBrands === 'function' && typeof BMYData !== 'undefined') {
                displayBrands(BMYData);
              }
              /* Show sidebar again */
              const sidebar = document.querySelector('.brands_card_side_bar');
              if (sidebar) sidebar.style.display = '';
              this.elements.resultsContainer.style.display = 'none';
              document.body.classList.remove('pf-results-active');
              window.history.pushState({}, '', window.location.pathname);
            });
            this.elements.resultsContainer.insertBefore(backBtn, this.elements.resultsContainer.firstChild);
          }
        }
        if (this.elements.carHeading) {
          const b = this.state.brandName, m = this.state.modelName, y = this.state.year, r = this.state.regNumber;
          this.elements.carHeading.innerHTML = `<span class="pf-car-heading__name">${b} ${m}</span><span class="pf-car-heading__sep"></span><span class="pf-car-heading__year">${y}</span>${r ? `<span class="pf-car-heading__reg">${r}</span>` : ''}`;
          this.elements.carHeading.style.display = '';
        }

        /* Fetch and render car hero image */
        if (!append) this._renderCarHero();

        const totalLoaded = this.productState.allHits.length;
        if (this.elements.resultsPagination) this.elements.resultsPagination.style.display = totalLoaded < this.productState.totalHits ? '' : 'none';

        if (!append) container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        /* Build inline compact search bar for dedicated page (desktop) */
        if (!append && document.body.classList.contains('template-page') && this.productState.totalHits > 0) {
          this._buildInlineSearch();
        }
      } catch (error) {
        console.error('Product search failed:', error);
        /* Auto-retry once with fresh Algolia client */
        if (!this._searchRetried) {
          this._searchRetried = true;
          if (this.config.productKey) {
            this.productClient = window.algoliasearch(this.config.appId, this.config.productKey);
            this.productIndex = this.productClient.initIndex(this.config.productIndex);
          }
          setTimeout(() => { this.searchProducts(filter, append); }, 500);
          return;
        }
        this._searchRetried = false;
        grid.innerHTML = '<div class="partfinder__no-results"><p>Kunne ikke laste produkter.</p><button type="button" class="pf-retry-btn" style="margin-top:12px;padding:10px 24px;background:rgba(var(--color-accent-1));color:#fff;border:none;border-radius:6px;cursor:pointer;font-weight:700;">Prøv igjen</button></div>';
        grid.querySelector('.pf-retry-btn')?.addEventListener('click', () => {
          this._searchRetried = false;
          this.searchProducts(this.productState.currentFilter, false);
        });
      }
    }

    renderProducts(append) {
      const grid = this.elements.resultsGrid;
      if (!grid) return;
      if (!append) {
        grid.innerHTML = '';
        grid.className = this.productState.viewMode === 'list'
          ? 'partfinder__results-grid pf-grid--list'
          : 'partfinder__results-grid pf-grid--cards';
      }

      const hits = append ? this.productState.allHits.slice(-this.productState.hitsPerPage) : this.productState.allHits;
      if (this.productState.allHits.length === 0) {
        grid.innerHTML = `
          <div class="partfinder__empty-state">
            <p class="partfinder__empty-sorry">Vi har dessverre ingen produkter til denne bilen for øyeblikket.</p>
            <h2 class="partfinder__empty-title">Fant du ikke det du lette etter, eller ønsker du et tilbud?</h2>
            <button type="button" class="button partfinder__empty-cta" open-popup="#pf-pristilbud-form" style="background:rgba(var(--color-accent-1));border-color:rgba(var(--color-accent-1));color:#fff;font-size:18px;padding:16px 40px;border-radius:8px;cursor:pointer;font-weight:700;">
              Send meg et pristilbud!
            </button>
          </div>
          <div class="form_popup_selection_form" id="pf-pristilbud-form" popup-wrapper="">
            <div class="form_overlay">&nbsp;</div>
            <div class="form_popup_data_selection">
              <div class="form-container_selection">
                <div class="form-header_Selection">
                  <button type="button" class="button button-tertiary button-drawer-header-dismiss dropdown-modal-toggle close-button" aria-label="Lukk" aria-expanded="false" style="margin-right:1rem;">×</button>
                  <h2>Pristilbud</h2>
                </div>
                <div class="form-content_selction_form">
                  <form method="post" action="/contact#pf_ContactForm" id="pf_ContactForm" accept-charset="UTF-8" class="isolate" novalidate="novalidate">
                    <input type="hidden" name="form_type" value="contact">
                    <input type="hidden" name="utf8" value="✓">
                    <input type="hidden" name="contact[car]" value="${this.state.brandName} ${this.state.modelName} ${this.state.year}">
                    <input type="hidden" name="contact[regnr_lookup]" value="${this.state.regNumber || ''}">
                    <div class="form_main_fields">
                      <input type="text" placeholder="Navn" name="contact[name]" required>
                      <input type="email" placeholder="E-post" name="contact[email]" required>
                      <input type="tel" placeholder="Telefon" name="contact[phone]" pattern="[0-9\\-]*" autocomplete="tel" required>
                      <input type="text" placeholder="Regnr" name="contact[Regnr]" value="${this.state.regNumber || ''}">
                      <input type="text" placeholder="Bilmerke" name="contact[Bilmerke]" value="${this.state.brandName || ''}">
                      <input type="text" placeholder="Modell" name="contact[Modell]" value="${this.state.modelName || ''}">
                      <input type="text" placeholder="Årsmodell" name="contact[Årsmodell]" value="${this.state.year || ''}">
                      <textarea placeholder="Kommentar" name="contact[Message]"></textarea>
                      <button type="submit" class="button button-primary submit-form_request">Send meg et pristilbud!</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>`;
        /* Wire up the popup open/close */
        const cta = grid.querySelector('[open-popup]');
        const popup = grid.querySelector('#pf-pristilbud-form');
        if (cta && popup) {
          cta.addEventListener('click', () => popup.classList.add('active'));
          popup.querySelector('.form_overlay')?.addEventListener('click', () => popup.classList.remove('active'));
          popup.querySelector('.close-button')?.addEventListener('click', () => popup.classList.remove('active'));
        }
        return;
      }

      if (!append) this.productState._renderedHandles = new Set();
      const rendered = this.productState._renderedHandles;
      hits.forEach(hit => {
        const key = hit.handle || hit.objectID || hit.sku;
        if (!key || rendered.has(key)) return;
        rendered.add(key);
        grid.appendChild(this.createProductCard(hit));
      });
    }

    createProductCard(hit) {
      const card = document.createElement('div');
      card.className = 'pf-product-card';

      const imageUrl = hit.image || (hit.images && hit.images[0]?.src) || '';
      const title = hit.title || '';
      const intro = this._getIntroduction(hit);
      const sku = hit.sku || '';
      const price = parseFloat(hit.price || 0);
      const compareAt = parseFloat(hit.compare_at_price || 0);
      const stock = this._getStockStatus(hit);
      const hasDiscount = compareAt > 0 && compareAt > price;
      const discountPercent = hasDiscount ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
      const handle = hit.handle || '';
      const variantId = hit.variants?.[0]?.node?.id?.replace('gid://shopify/ProductVariant/', '') || '';
      const formattedPrice = price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const formattedCompare = compareAt.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const warehouseInfo = this._getWarehouseInfo(hit);

      /* Stock HTML */
      let stockHtml = '';
      if (stock.shopifyStock >= 1 || (warehouseInfo.quantity !== null && warehouseInfo.quantity >= 1)) {
        stockHtml = '<div class="pf-card__stock pf-card__stock--in"><span class="pf-card__stock-dot"></span>På lager</div>';
      } else if (warehouseInfo.expectedDate) {
        stockHtml = `<div class="pf-card__stock pf-card__stock--expected"><span class="pf-card__stock-dot"></span>Forventet: ${warehouseInfo.expectedDate}</div>`;
      } else if (warehouseInfo.orderDays !== null) {
        stockHtml = `<div class="pf-card__stock pf-card__stock--expected"><span class="pf-card__stock-dot"></span>${warehouseInfo.orderDays} dager</div>`;
      } else if (warehouseInfo.rawStatus) {
        stockHtml = '<div class="pf-card__stock pf-card__stock--expected"><span class="pf-card__stock-dot"></span>På vei</div>';
      } else if (warehouseInfo.quantity !== null && warehouseInfo.quantity <= 0) {
        stockHtml = '<div class="pf-card__stock pf-card__stock--out"><span class="pf-card__stock-dot"></span>Ikke på lager</div>';
      }

      card.innerHTML = `
        <a href="/products/${handle}" class="pf-card__media">
          <img class="pf-card__img" src="${imageUrl}" alt="${this._sanitize(title)}" loading="lazy" onerror="this.onerror=null;this.src='https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png?v=1530129081';">
          ${hasDiscount ? `<span class="pf-card__badge">-${discountPercent}%</span>` : ''}
        </a>
        <div class="pf-card__body">
          <div class="pf-card__info-stack">
            <h3 class="pf-card__title"><a href="/products/${handle}">${this._sanitize(title)}</a></h3>
            ${intro && intro !== 'No introduction available' ? `<p class="pf-card__intro">${this._sanitize(intro)}</p>` : ''}
            ${sku ? `<span class="pf-card__sku">Varenr: ${this._sanitize(sku)}</span>` : ''}
            ${stockHtml}
          </div>
          <div class="pf-card__price-row">
            ${hasDiscount ? `
              <s class="pf-card__price-compare">${formattedCompare} kr</s>
              <span class="pf-card__price pf-card__price--sale">${formattedPrice} kr</span>
            ` : `
              <span class="pf-card__price">${formattedPrice} kr</span>
            `}
            <span class="pf-card__vat">inkl. mva</span>
          </div>
          <div class="pf-card__actions pf-card__actions--grid" data-pf-atc-slot></div>
          <div class="pf-card__actions pf-card__actions--list" data-pf-atc-slot-list></div>
        </div>`;

      /* Build add-to-cart forms — matching theme's product-form.js behavior */
      const buildAtcForm = (isListView) => {
        const wrapper = document.createElement('div');
        let formHtml = '<form method="post" action="/cart/add" novalidate data-type="add-to-cart-form">';
        formHtml += '<input type="hidden" name="form_type" value="product">';
        formHtml += '<input type="hidden" name="utf8" value="✓">';
        formHtml += '<input type="hidden" name="id" value="' + variantId + '">';

        if (isListView) {
          formHtml += '<div class="pf-card__qty-row">';
          formHtml += '<div class="pf-card__qty">';
          formHtml += '<button type="button" class="pf-card__qty-btn" data-pf-minus>−</button>';
          formHtml += '<input type="number" name="quantity" class="pf-card__qty-input" value="1" min="1">';
          formHtml += '<button type="button" class="pf-card__qty-btn" data-pf-plus>+</button>';
          formHtml += '</div>';
          formHtml += '<button type="submit" name="add" class="pf-card__atc-btn"><span>Legg til i handlekurv</span><div class="button-overlay-spinner hidden"><span class="spinner-xs"></span></div></button>';
          formHtml += '</div>';
        } else {
          formHtml += '<button type="submit" name="add" class="pf-card__atc-btn"><span>Legg i handlekurv</span><div class="button-overlay-spinner hidden"><span class="spinner-xs"></span></div></button>';
        }
        formHtml += '</form>';
        wrapper.innerHTML = formHtml;
        const form = wrapper.firstElementChild;

        form.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const btn = form.querySelector('.pf-card__atc-btn');
          const vid = form.querySelector('input[name="id"]').value;
          const qty = form.querySelector('input[name="quantity"]')?.value || '1';
          const originalText = isListView ? 'Legg til i handlekurv' : 'Legg i handlekurv';

          btn.setAttribute('aria-disabled', 'true');
          btn.querySelector('span').textContent = '✓ Lagt til!';
          btn.style.background = '#16793a';
          btn.style.borderColor = '#16793a';
          btn.style.color = '#fff';

          /* Fire everything without waiting */
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: parseInt(vid), quantity: parseInt(qty) })
          }).then(r => r.json()).then(data => {
            if (data.status) {
              btn.querySelector('span').textContent = data.description || 'Feil';
              btn.style.background = '#c00';
              btn.style.borderColor = '#c00';
              return;
            }
            /* Refresh and open drawer */
            const minicart = document.querySelector('mini-cart');
            if (minicart) {
              const ids = minicart.getSectionsToRender().map(s => s.section).join(',');
              fetch('/?sections=' + ids).then(r => r.json()).then(sections => {
                minicart.renderContents({ sections });
                minicart.open(btn);
              });
            }
          }).catch(() => {
            btn.querySelector('span').textContent = 'Feil';
            btn.style.background = '#c00';
            btn.style.borderColor = '#c00';
          });

          /* Reset button after delay regardless */
          setTimeout(() => {
            btn.removeAttribute('aria-disabled');
            btn.querySelector('span').textContent = originalText;
            btn.style.background = '';
            btn.style.borderColor = '';
            btn.style.color = '';
            btn.style.color = '#fff';
          }, 1200);
        });

        return form;
      };

      const gridSlot = card.querySelector('[data-pf-atc-slot]');
      const listSlot = card.querySelector('[data-pf-atc-slot-list]');
      if (gridSlot) gridSlot.appendChild(buildAtcForm(false));
      if (listSlot) listSlot.appendChild(buildAtcForm(true));

      /* Quantity +/- handlers */
      card.querySelectorAll('[data-pf-minus]').forEach(btn => {
        btn.addEventListener('click', () => {
          const inp = btn.closest('.pf-card__qty').querySelector('.pf-card__qty-input');
          if (inp && parseInt(inp.value) > 1) inp.value = parseInt(inp.value) - 1;
        });
      });
      card.querySelectorAll('[data-pf-plus]').forEach(btn => {
        btn.addEventListener('click', () => {
          const inp = btn.closest('.pf-card__qty').querySelector('.pf-card__qty-input');
          if (inp) inp.value = parseInt(inp.value) + 1;
        });
      });

      return card;
    }

    /* ══════════ FACET FILTERS SIDEBAR ══════════ */

    renderFacets(facets) {
      const sidebar = this.elements.filtersSidebar;
      if (!sidebar) return;
      sidebar.innerHTML = '';

      const checkSvg = '<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.5303 9.53033C17.8232 9.23744 17.8232 8.76256 17.5303 8.46967C17.2374 8.17678 16.7626 8.17678 16.4697 8.46967L17.5303 9.53033ZM9.99998 16L9.46965 16.5304C9.76255 16.8232 10.2374 16.8232 10.5303 16.5303L9.99998 16ZM7.53027 12.4697C7.23737 12.1768 6.7625 12.1768 6.46961 12.4697C6.17671 12.7626 6.17672 13.2374 6.46961 13.5303L7.53027 12.4697ZM16.4697 8.46967L9.46965 15.4697L10.5303 16.5303L17.5303 9.53033L16.4697 8.46967ZM6.46961 13.5303L9.46965 16.5304L10.5303 15.4697L7.53027 12.4697L6.46961 13.5303Z"></path></svg>';
      const VISIBLE_LIMIT = 8;

      const skipCategories = new Set([
        'BILSTEREO', 'VÅRE MERKER', 'Bilunderholdning_1', 'Bilunderholdning',
        this.state.brandName, this.state.modelName
      ]);

      /* Mobile drawer header */
      const mobileHeader = document.createElement('div');
      mobileHeader.className = 'pf-filter-drawer-header';
      mobileHeader.innerHTML = '<span>Filtrer produkter</span><button type="button" class="pf-filter-drawer-close" aria-label="Lukk">&times;</button>';
      mobileHeader.querySelector('.pf-filter-drawer-close').addEventListener('click', () => {
        this._closeFilterDrawer();
      });
      sidebar.appendChild(mobileHeader);

      /* Facet content wrapper (for scroll between header and footer) */
      const facetBody = document.createElement('div');
      facetBody.className = 'pf-filter-drawer-body';

      /* Helper: build a single facet group */
      const buildFacetGroup = (label, entries, facetKey) => {
        if (!entries || entries.length === 0) return;
        const activeValues = this.productState.activeFacets[facetKey] || [];

        let html = '<div class="js-filter"><div><div class="facet">';
        html += `<button class="facet-toggle" type="button" aria-expanded="true"><span>${label}</span><span class="angle angle-down"><span class="angle-icon"></span></span></button>`;
        html += '<div class="facet-content" style="display:block"><ul class="facet-options-list custom-filters-list">';

        entries.forEach(([name, count, displayName], idx) => {
          const isChecked = activeValues.includes(name);
          const hiddenStyle = idx >= VISIBLE_LIMIT ? ' style="display:none"' : '';
          const label = displayName || name;
          html += `<li class="facet-options-list-item"${hiddenStyle}>
            <span class="checkbox-facet">
              <label class="checkbox-facet-label">
                <input type="checkbox" class="new-filter-input" data-facet-key="${facetKey}" data-facet-value="${name.replace(/"/g, '&quot;')}" ${isChecked ? 'checked' : ''}>
                <span class="checkbox-facet-check"></span>
                ${checkSvg}
                <span class="checkbox-facet-label-text">${label}</span>
                <span class="checkbox-facet-label-count">${count}</span>
              </label>
            </span>
          </li>`;
        });

        html += '</ul>';
        if (entries.length > VISIBLE_LIMIT) {
          html += '<button type="button" class="pf-facet-show-more">+ Vis mer</button>';
        }
        html += '</div></div></div></div>';

        const frag = document.createElement('div');
        frag.innerHTML = html;
        const wrapper = frag.firstElementChild;

        wrapper.querySelector('.facet-toggle').addEventListener('click', function () {
          const content = wrapper.querySelector('.facet-content');
          const expanded = this.getAttribute('aria-expanded') === 'true';
          this.setAttribute('aria-expanded', String(!expanded));
          if (content) content.style.display = expanded ? 'none' : 'block';
        });

        wrapper.querySelectorAll('input[type="checkbox"]').forEach(cb => {
          cb.addEventListener('change', () => {
            const fk = cb.dataset.facetKey;
            const fv = cb.dataset.facetValue;
            if (!this.productState.activeFacets[fk]) this.productState.activeFacets[fk] = [];
            if (cb.checked) this.productState.activeFacets[fk].push(fv);
            else this.productState.activeFacets[fk] = this.productState.activeFacets[fk].filter(v => v !== fv);
            this.productState.currentPage = 0;
            this.productState.allHits = [];
            this.searchProducts(this.productState.currentFilter, false);
          });
        });

        const showMore = wrapper.querySelector('.pf-facet-show-more');
        if (showMore) {
          showMore.addEventListener('click', () => {
            const items = wrapper.querySelectorAll('.facet-options-list-item');
            const anyHidden = Array.from(items).some((el, i) => i >= VISIBLE_LIMIT && el.style.display === 'none');
            items.forEach((el, i) => { if (i >= VISIBLE_LIMIT) el.style.display = anyHidden ? '' : 'none'; });
            showMore.textContent = anyHidden ? '- Vis færre' : '+ Vis mer';
          });
        }

        facetBody.appendChild(wrapper);
      };

      /* ── Tags: group using storeMetafields if available ── */
      const tagsFacet = facets['tags'];
      const metaFilters = window.storeMetafields?.facets?.filters;

      if (tagsFacet && metaFilters && Array.isArray(metaFilters)) {
        /* Build lowercase lookup map: lowercase tag → {originalName, count} */
        const tagsLower = {};
        Object.entries(tagsFacet).forEach(([name, count]) => {
          if (count > 0 && !skipCategories.has(name) && name.length < 50) {
            tagsLower[name.toLowerCase()] = { name, count };
          }
        });

        metaFilters.forEach(metaFilter => {
          if (!metaFilter.tags || !Array.isArray(metaFilter.tags)) return;
          /* Skip "Velg bilmerke" when showing car-specific results */
          if (metaFilter.title === 'Velg bilmerke' || metaFilter.label === 'Velg bilmerke') return;

          const matched = [];
          metaFilter.tags.forEach(mt => {
            const key = (mt.tag || '').toLowerCase();
            const found = tagsLower[key];
            if (found) {
              /* Use tag_label as display name if available, otherwise original Algolia name */
              const displayName = (mt.tag_label && mt.tag_label.trim()) ? mt.tag_label : found.name;
              matched.push({ name: found.name, displayName, count: found.count });
            }
          });

          if (matched.length === 0) return;

          matched.sort((a, b) => {
            const nA = parseFloat(a.displayName), nB = parseFloat(b.displayName);
            if (!isNaN(nA) && !isNaN(nB)) return nA - nB;
            if (!isNaN(nA)) return -1;
            if (!isNaN(nB)) return 1;
            return a.displayName.localeCompare(b.displayName, 'no', { sensitivity: 'base' });
          });

          /* Build entries using original Algolia name as value (for filtering) but displayName for label */
          const entries = matched.map(m => [m.name, m.count, m.displayName]);
          if (entries.length > 0) {
            buildFacetGroup(metaFilter.label, entries, 'tags');
          }
        });
      } else if (tagsFacet) {
        /* Fallback: show tags as single group */
        const entries = Object.entries(tagsFacet)
          .filter(([name, count]) => count > 0 && !skipCategories.has(name) && name.length < 50)
          .sort((a, b) => b[1] - a[1]);
        buildFacetGroup('Type', entries, 'tags');
      }

      /* ── Vendor (Varemerke) ── */
      if (facets['vendor']) {
        const entries = Object.entries(facets['vendor'])
          .filter(([name, count]) => count > 0 && name.length < 50)
          .sort((a, b) => a[0].localeCompare(b[0], 'no', { sensitivity: 'base' }));
        if (entries.length > 0) buildFacetGroup('Varemerke', entries, 'vendor');
      }

      sidebar.appendChild(facetBody);

      /* Mobile drawer footer */
      const mobileFooter = document.createElement('div');
      mobileFooter.className = 'pf-filter-drawer-footer';
      mobileFooter.innerHTML = `
        <button type="button" class="pf-filter-drawer-clear">Fjern alt</button>
        <button type="button" class="pf-filter-drawer-apply">Søk</button>
      `;
      mobileFooter.querySelector('.pf-filter-drawer-clear').addEventListener('click', () => {
        this.productState.activeFacets = {};
        this.productState.currentPage = 0;
        this.productState.allHits = [];
        this._closeFilterDrawer();
        this.searchProducts(this.productState.currentFilter, false);
      });
      mobileFooter.querySelector('.pf-filter-drawer-apply').addEventListener('click', () => {
        this._closeFilterDrawer();
      });
      sidebar.appendChild(mobileFooter);
    }

    /* ══════════ REGISTRATION CACHE ══════════ */

    static MAX_REG_CACHE = 50;

    getCachedRegistration(cacheKey) {
      try {
        const raw = localStorage.getItem(cacheKey); if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.data || !parsed.expiresAt) return null;
        if (Date.now() > parsed.expiresAt) { localStorage.removeItem(cacheKey); this._removeFromRegIndex(cacheKey); return null; }
        return parsed.data;
      } catch { return null; }
    }

    cacheRegistration(cacheKey, data) {
      try {
        const isStrong = !!(data && data.status === true && data.brand_name && data.model_name && data.year);
        const ttl = isStrong ? 1000*60*60*24*7 : 1000*60*60;
        localStorage.setItem(cacheKey, JSON.stringify({ data, expiresAt: Date.now() + ttl }));
        this._addToRegIndex(cacheKey); this._evictOldRegEntries();
      } catch (e) { console.warn('Could not cache:', e); }
    }

    _getRegIndex() { try { const r = localStorage.getItem('regLookupIndex'); return r ? JSON.parse(r) : []; } catch { return []; } }
    _addToRegIndex(key) { try { let i = this._getRegIndex().filter(k => k !== key); i.push(key); localStorage.setItem('regLookupIndex', JSON.stringify(i)); } catch {} }
    _removeFromRegIndex(key) { try { const i = this._getRegIndex().filter(k => k !== key); localStorage.setItem('regLookupIndex', JSON.stringify(i)); } catch {} }
    _evictOldRegEntries() { try { let i = this._getRegIndex(); while (i.length > PartFinder.MAX_REG_CACHE) { localStorage.removeItem(i.shift()); } localStorage.setItem('regLookupIndex', JSON.stringify(i)); } catch {} }

    /* ══════════ REGISTRATION DATA — INSTANT ══════════ */

    applyRegistrationData(data, regNumber) {
      if (data.brand_name) this.state.brandName = data.brand_name;
      if (data.brand_id) this.state.brandId = String(data.brand_id);
      if (data.model_name) this.state.modelName = data.model_name;
      if (data.model_id) this.state.modelId = String(data.model_id);
      if (data.year) this.state.year = String(data.year);

      if (this.elements.brandInput) this.elements.brandInput.value = data.brand_name || '';
      if (this.elements.modelInput) { if (data.model_name) this.elements.modelInput.disabled = false; this.elements.modelInput.value = data.model_name || ''; }
      if (this.elements.yearInput) { if (data.year) this.elements.yearInput.disabled = false; this.elements.yearInput.value = data.year || ''; }
      this.updateBreadcrumbs();

      if (data.brand_name && data.model_name && data.year && data.status === true) {
        localStorage.setItem('Selected-model', data.model_name);
        localStorage.setItem('Selected-brand', data.brand_name);
        localStorage.setItem('Selected-year', data.year);
        localStorage.setItem('Selected-num-plate', regNumber);
        this.persistSelectedCar({ brand_id: data.brand_id, brand_name: data.brand_name, model_id: data.model_id, model_name: data.model_name, year: data.year });

        const filter = `brand_model_year:${data.brand_name}/${data.model_name}/${data.year}`;
        const isFirstRegSearch = !this.productState.currentFilter;
        this.productState.currentFilter = filter;
        this.productState.currentPage = 0;
        this.productState.allHits = [];
        this.productState.activeFacets = {};
        const regFilterUrl = this.buildFilterRedirectUrl(data.brand_name, data.model_name, data.year);
        if (isFirstRegSearch) {
          window.history.replaceState({ partfinder: false }, '', window.location.pathname);
        }
        window.history.pushState({ partfinder: true }, '', regFilterUrl);
        this.searchProducts(filter, false);
        return;
      }

      if (data.status === false && data.gpt_response?.brand && data.gpt_response?.model && data.gpt_response?.year) {
        localStorage.setItem('Selected-model', data.gpt_response.model);
        localStorage.setItem('Selected-brand', data.gpt_response.brand);
        localStorage.setItem('Selected-year', data.gpt_response.year);
        const filter = `brand_model_year:${data.gpt_response.brand}/${data.gpt_response.model}/${data.gpt_response.year}`;
        this.productState.currentFilter = filter; this.productState.currentPage = 0; this.productState.allHits = []; this.productState.activeFacets = {};
        this.searchProducts(filter, false);
        return;
      }

      this.showToast('Vi fant dessverre ikke bilen din med reg.nr. Prøv rullegardinmenyen.');
    }

    /* ══════════ URL BUILDERS & HELPERS ══════════ */

    normalizeRegistration(value) { return String(value || '').replace(/\s+/g, '').toUpperCase().trim(); }

    buildFilterRedirectUrl(brand, model, year) {
      /* If we're on a page with our partfinder section (e.g. homepage),
         keep the current path so back-navigation returns here.
         Only use collectionUrl if we lack a productClient (= redirect mode). */
      const isInlinePage = !!this.productClient && !window.location.pathname.startsWith('/collections/');
      const basePath = isInlinePage ? window.location.pathname : String(this.config.collectionUrl || '/collections/all').replace(/\/$/, '');
      const params = new URLSearchParams();
      params.set('pf_brand', brand);
      params.set('pf_model', model);
      params.set('pf_year', year);
      return `${basePath}?${params.toString()}`;
    }

    buildFallbackRedirectUrl(brand, model, year) {
      const baseUrl = String(this.config.collectionUrl || '/collections/all').replace(/\/$/, '');
      return `${baseUrl}/${String(brand).replace(/[^a-zA-Z0-9_]/g, '-')}+model_${String(model).replace(/[^a-zA-Z0-9_]/g, '-')}+year_${year}`;
    }

    persistSelectedCar(car) {
      const key = 'carsData'; let items = [];
      try { const existing = localStorage.getItem(key); items = existing ? JSON.parse(existing) : []; } catch { items = []; }
      items.push(car); if (items.length > 10) items = items.slice(items.length - 10);
      localStorage.setItem(key, JSON.stringify(items));
    }

    /* ══════════ TOAST ══════════ */

    showToast(message) {
      const toast = this.elements.toast; if (!toast) return;
      toast.textContent = message; toast.classList.add('is-visible');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { toast.classList.remove('is-visible'); toast.textContent = ''; }, 2200);
    }

    /* ══════════ BREADCRUMBS & MOBILE ══════════ */

    updateBreadcrumbs() {
      const bc = this.elements.breadcrumbs; if (!bc) return;
      const brand = bc.querySelector('.brand'), model = bc.querySelector('.model'), year = bc.querySelector('.year');
      const seps = bc.querySelectorAll('.sep');
      if (brand) brand.textContent = this.state.brandName || '';
      if (model) model.textContent = this.state.modelName || '';
      if (year) year.textContent = this.state.year || '';
      if (seps[0]) seps[0].style.display = (this.state.brandName && this.state.modelName) ? 'inline' : 'none';
      if (seps[1]) seps[1].style.display = (this.state.modelName && this.state.year) ? 'inline' : 'none';
    }

    setMobileTitle(text) { if (this.elements.mobileTitle) this.elements.mobileTitle.textContent = text; }

    openMobile() {
      const wrapper = this.elements.manualWrapper;
      if (wrapper && wrapper.parentElement !== this.body) {
        this._originalParent = wrapper.parentElement;
        this._originalNextSibling = wrapper.nextSibling;
        this.body.appendChild(wrapper);
      }
      wrapper?.classList.add('is-active');
      this.body.classList.add('partfinder-mobile-open');
      this.body.style.overflow = 'hidden';
      this.showMobileStep('brand');
      this.setMobileTitle('Velg Bilmerke');
      this.updateBreadcrumbs();
    }

    closeMobile() {
      const wrapper = this.elements.manualWrapper;
      wrapper?.classList.remove('is-active');
      this.body.classList.remove('partfinder-mobile-open');
      this.body.style.overflow = '';
      this.clearMobileSteps();
      this.setMobileTitle('Velg Bilmerke');
      this.updateBreadcrumbs();
      if (wrapper && this._originalParent) {
        if (this._originalNextSibling) {
          this._originalParent.insertBefore(wrapper, this._originalNextSibling);
        } else {
          this._originalParent.appendChild(wrapper);
        }
      }
    }

    clearMobileSteps() { [this.elements.stepBrand, this.elements.stepModel, this.elements.stepYear].forEach(s => s?.classList.remove('is-active')); }

    showMobileStep(stepName) {
      this.clearMobileSteps();
      if (stepName === 'brand') this.elements.stepBrand?.classList.add('is-active');
      if (stepName === 'model') this.elements.stepModel?.classList.add('is-active');
      if (stepName === 'year') this.elements.stepYear?.classList.add('is-active');
    }

    goBack(stepName) {
      if (stepName === 'brand') {
        this.state.modelId = ''; this.state.modelName = ''; this.state.year = '';
        if (this.elements.modelInput) this.elements.modelInput.value = '';
        if (this.elements.yearInput) this.elements.yearInput.value = '';
        if (this.elements.yearInput) this.elements.yearInput.disabled = true;
        this._clearList(this.elements.yearList);
        this.setMobileTitle('Velg Bilmerke'); this.updateBreadcrumbs(); this.showMobileStep('brand'); return;
      }
      if (stepName === 'model') {
        this.state.year = ''; if (this.elements.yearInput) this.elements.yearInput.value = '';
        this.setMobileTitle('Velg Modell'); this.updateBreadcrumbs(); this.showMobileStep('model');
      }
    }

    /* ══════════ KEYBOARD NAVIGATION ══════════ */

    handleListKeyboard(event, input) {
      const wrapper = input.closest('[data-step]'); if (!wrapper) return;
      const list = wrapper.querySelector('.partfinder__list'); if (!list) return;
      const visibleItems = Array.from(list.querySelectorAll('li')).filter(i => i.style.display !== 'none');
      if (!visibleItems.length) return;
      this.activeList = list;

      if (event.key === 'ArrowDown') { event.preventDefault(); this.activeIndex = (this.activeIndex + 1) % visibleItems.length; this.applyFocusState(visibleItems); this._setActiveDescendant(input, visibleItems[this.activeIndex]?.id || null); return; }
      if (event.key === 'ArrowUp') { event.preventDefault(); this.activeIndex = (this.activeIndex - 1 + visibleItems.length) % visibleItems.length; this.applyFocusState(visibleItems); this._setActiveDescendant(input, visibleItems[this.activeIndex]?.id || null); return; }
      if (event.key === 'Enter' && this.activeIndex >= 0) { event.preventDefault(); visibleItems[this.activeIndex]?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true })); }
      if (event.key === 'Escape') { event.preventDefault(); this.closeAllDropdowns(); }
    }

    clearFocusedItems(list) { list.querySelectorAll('li').forEach(i => i.classList.remove('is-focused')); }

    applyFocusState(items) {
      if (!this.activeList) return;
      this.clearFocusedItems(this.activeList);
      const item = items[this.activeIndex]; if (!item) return;
      item.classList.add('is-focused'); item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  function initPartFinders() {
    document.querySelectorAll('[data-partfinder]').forEach((root) => {
      if (root._pfInitialized) return;
      const finder = new PartFinder(root);
      if (finder.isReady) {
        finder.init();
        window._pfInstance = finder;
        root._pfInitialized = true;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPartFinders);
  } else {
    initPartFinders();
  }
  /* Observe header visibility for sticky sidebar offset */
  function initSidebarHeaderSync() {
    const header = document.querySelector('.section-header-sticky, .shopify-section-header, [class*="header"]');
    if (!header) return;
    const update = () => {
      const isHidden = header.classList.contains('section-header-hidden');
      const headerH = isHidden ? 0 : header.offsetHeight;
      document.documentElement.style.setProperty('--pf-sidebar-top', (headerH + 16) + 'px');
    };
    update();
    const obs = new MutationObserver(update);
    obs.observe(header, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('scroll', update, { passive: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSidebarHeaderSync);
  } else {
    initSidebarHeaderSync();
  }
})();